import React, { useState } from 'react';
import clsx from 'clsx';
import { useAppSelector } from '../../store/hooks';

interface ErrorObject {
  type?: string;
  message?: string;
  details?: any;
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string | ErrorObject | null;
  /** When true, still apply error styles but do not render the error message in flow (caller positions it). */
  hideErrorMessage?: boolean;
  helpText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  variant?: 'default' | 'mirsal' | 'elegant';
  inputSize?: 'sm' | 'md' | 'lg';
  inlineLabel?: boolean;
  forceDarkMode?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    label, 
    error, 
    hideErrorMessage = false,
    helpText, 
    leftIcon, 
    rightIcon, 
    variant = 'mirsal',
    inputSize = 'md',
    inlineLabel = false,
    forceDarkMode,
    id, 
    ...props 
  }, ref) => {
    const { theme } = useAppSelector((state) => state.ui);
    const [isFocused, setIsFocused] = useState(false);
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    
    // Use forceDarkMode if provided, otherwise use theme from Redux
    const effectiveTheme = forceDarkMode ? 'dark' : theme;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      props.onChange?.(e);
    };

    // Size variants
    const sizeClasses = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-2.5 text-sm',
      lg: 'px-4 py-3 text-base'
    };

    const iconSizeClasses = {
      sm: leftIcon ? 'pl-9' : '',
      md: leftIcon ? 'pl-10' : '',
      lg: leftIcon ? 'pl-12' : ''
    };

    const rightIconSizeClasses = {
      sm: rightIcon ? 'pr-9' : '',
      md: rightIcon ? 'pr-10' : '',
      lg: rightIcon ? 'pr-12' : ''
    };

    // Variant styles
    const variantClasses = {
      default: {
        base: effectiveTheme === 'dark' 
          ? 'bg-gray-800 border border-gray-600 text-white placeholder-gray-400' 
          : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400',
        focus: effectiveTheme === 'dark'
          ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
          : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        error: 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500',
        hover: ''
      },
      mirsal: {
        base: effectiveTheme === 'dark'
          ? 'bg-gray-800 border-2 border-gray-600 text-white placeholder-gray-400'
          : 'bg-white border-2 border-gray-200 text-gray-900 placeholder-gray-400',
        focus: 'focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green',
        error: effectiveTheme === 'dark'
          ? 'border-red-500 text-red-300 placeholder-red-400/60 focus:ring-red-500 focus:border-red-500'
          : 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500',
        hover: effectiveTheme === 'dark' ? 'hover:border-gray-500' : 'hover:border-gray-300'
      },
      elegant: {
        base: effectiveTheme === 'dark'
          ? 'bg-gray-800 border-0 border-b-2 border-gray-600 text-white placeholder-gray-400 rounded-t-lg'
          : 'bg-gray-50 border-0 border-b-2 border-gray-200 text-gray-900 placeholder-gray-500 rounded-t-lg',
        focus: effectiveTheme === 'dark'
          ? 'focus:ring-0 focus:border-mirsal-green focus:bg-gray-700'
          : 'focus:ring-0 focus:border-mirsal-green focus:bg-white',
        error: effectiveTheme === 'dark'
          ? 'border-red-500 text-red-300 placeholder-red-400/60 focus:border-red-500'
          : 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500',
        hover: effectiveTheme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
      }
    };

    const currentVariant = variantClasses[variant];

    return (
      <div className={clsx(inlineLabel ? 'flex items-center gap-3' : 'space-y-2')}>
        {label && (
          <label 
            htmlFor={inputId} 
            className={clsx(
              'font-medium transition-colors duration-200 flex-shrink-0',
              inlineLabel ? 'w-32' : 'block',
              effectiveTheme === 'dark' 
                ? (variant === 'mirsal' ? 'text-gray-200' : 'text-gray-300')
                : (variant === 'mirsal' ? 'text-gray-800' : 'text-gray-700'),
              error ? 'text-red-600' : '',
              inputSize === 'sm' ? 'text-xs' : inputSize === 'lg' ? 'text-base' : 'text-sm'
            )}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <div className={clsx('relative group', inlineLabel && 'flex-1 min-w-0')}>
          {leftIcon && (
            <div className={clsx(
              'absolute inset-y-0 left-0 flex items-center pointer-events-none transition-colors duration-200',
              inputSize === 'sm' ? 'pl-2.5' : inputSize === 'lg' ? 'pl-4' : 'pl-3'
            )}>
              <span className={clsx(
                'transition-colors duration-200',
                isFocused && variant === 'mirsal' ? 'text-mirsal-green' : (effectiveTheme === 'dark' ? 'text-gray-400' : 'text-gray-400'),
                error ? 'text-red-400' : '',
                inputSize === 'sm' ? 'text-sm' : 'text-sm'
              )}>
                {leftIcon}
              </span>
            </div>
          )}
          
          <input
            id={inputId}
            ref={ref}
            className={clsx(
              // Base styles
              'block w-full transition-all duration-200 ease-in-out',
              'focus:outline-none focus:ring-offset-0',
              
              // Size styles
              sizeClasses[inputSize],
              iconSizeClasses[inputSize],
              rightIconSizeClasses[inputSize],
              
              // Variant styles
              currentVariant.base,
              !error && currentVariant.focus,
              !error && currentVariant.hover,
              error && currentVariant.error,
              
              // Border radius based on variant
              variant === 'elegant' ? 'rounded-t-lg' : 'rounded-lg',
              
              // Shadow and effects
              variant === 'mirsal' && 'shadow-sm hover:shadow-md focus:shadow-lg',
              variant === 'default' && 'shadow-sm',
              
              // Animation effects
              isFocused && variant === 'mirsal' && 'transform scale-[1.02]',
              
              className
            )}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...props}
          />
          
          {rightIcon && (
            <div className={clsx(
              'absolute inset-y-0 right-0 flex items-center transition-colors duration-200',
              inputSize === 'sm' ? 'pr-2.5' : inputSize === 'lg' ? 'pr-4' : 'pr-3'
            )}>
              <span className={clsx(
                'transition-colors duration-200',
                isFocused && variant === 'mirsal' ? 'text-mirsal-green' : (effectiveTheme === 'dark' ? 'text-gray-400' : 'text-gray-400'),
                error ? 'text-red-400' : '',
                inputSize === 'sm' ? 'text-sm' : 'text-sm'
              )}>
                {rightIcon}
              </span>
            </div>
          )}

          {/* Focus indicator for Mirsal variant — hide when error so green bar doesn't clash with red border */}
          {variant === 'mirsal' && !error && (
            <div className={clsx(
              'absolute bottom-0 left-0 h-0.5 bg-mirsal-green transition-all duration-300 ease-out',
              isFocused ? 'w-full opacity-100' : 'w-0 opacity-0'
            )} />
          )}
        </div>

        {/* Error Message */}
        {error && !hideErrorMessage && (() => {
          const errorMessage = typeof error === 'string' 
            ? error 
            : error?.message || error?.details || 'An error occurred';
          
          return (
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className={clsx(
                'text-red-600 dark:text-red-400 font-medium animate-fade-in',
                inputSize === 'sm' ? 'text-xs' : 'text-sm'
              )}>
                {errorMessage}
              </p>
            </div>
          );
        })()}

        {/* Help Text */}
        {helpText && !error && (
          <p className={clsx(
            'text-gray-500 transition-colors duration-200',
            isFocused && variant === 'mirsal' ? 'text-gray-600' : '',
            inputSize === 'sm' ? 'text-xs' : 'text-sm'
          )}>
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
